import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import useWizard from '@components/Wizard/useWizard';
import { Card } from '@cards/Card';
import { FEATURE_FORM, FEATURE_QUESTIONNAIRE, FEATURE_TASK } from '@constants';
import { getContentId, useRuleContentRefs } from '@pathways/hooks/useRuleContentRefs';
import AddFormToRole from './AddFormToRole';

function CareTeamPermissions() {
  const { t } = useTranslation();
  const [roleRules, setRoleRules] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const { form, formData } = useWizard();
  const allRules = useMemo(() => {
    const stagedRules = formData.stagedRules?.stages?.flatMap(stage => stage.rules || []) || [];
    const indexEventRules =
      formData.indexEventRules?.indexEvents?.flatMap(ie => ie.rules || []) || [];
    return [...stagedRules, ...indexEventRules];
  }, [formData.stagedRules, formData.indexEventRules]);
  const { rules: hydratedRules } = useRuleContentRefs(allRules);

  const updateRoleRulesData = useCallback(
    data => {
      setRoleRules(data);
      form.setFieldsValue({ careTeamPermissions: data });
    },
    [form],
  );

  const extractRolesData = useCallback(rolesData => {
    if (!rolesData) return null;

    let current = rolesData;
    while (
      current &&
      typeof current === 'object' &&
      current.roles &&
      !Array.isArray(current.roles)
    ) {
      current = current.roles;
    }

    return current;
  }, []);

  const allAvailableContent = useMemo(() => {
    const contentMap = new Map();
    hydratedRules.forEach(rule => {
      if (
        [FEATURE_FORM, FEATURE_QUESTIONNAIRE, FEATURE_TASK].includes(rule.what) &&
        rule.whatDetail
      ) {
        const contentId = getContentId(rule.whatDetail);
        if (contentId && !contentMap.has(contentId)) {
          contentMap.set(contentId, {
            ...rule.whatDetail,
            what: rule.what,
            contentRef: rule.whatDetail.content,
          });
        }
      }
    });

    return Array.from(contentMap.values());
  }, [hydratedRules]);

  const callToActionList = useMemo(() => {
    return formData.callToAction || [];
  }, [formData.callToAction]);

  const contentRefMap = useMemo(() => {
    const map = new Map();
    allAvailableContent.forEach(content => {
      if (content.contentRef) {
        map.set(content.contentRef, content);
      }
    });
    return map;
  }, [allAvailableContent]);

  const ctaMap = useMemo(() => {
    const map = new Map();
    callToActionList.forEach(cta => {
      map.set(cta.id, cta);
    });
    return map;
  }, [callToActionList]);

  const hydratePermissions = useCallback(
    permissions => {
      if (!permissions) return [];
      return permissions.map(permission => {
        if (permission.type === 'cta' && !permission.callToAction && permission.id) {
          return {
            ...permission,
            callToAction: ctaMap.get(permission.id),
          };
        }
        if (permission.type === 'rule' && !permission.form && permission.contentRef) {
          return {
            ...permission,
            form: contentRefMap.get(permission.contentRef),
          };
        }
        return permission;
      });
    },
    [contentRefMap, ctaMap],
  );

  useEffect(() => {
    if (isInitialized) return;
    if (!formData.careTeamRoles) return;

    const extractedRolesData = extractRolesData(formData.careTeamRoles);
    if (!extractedRolesData) return;

    const roleKeys = Object.keys(extractedRolesData)
      .filter(key => key.match(/role-\d{2}--name/))
      .sort();

    if (roleKeys.length === 0) return;

    const savedRoleRules = formData.careTeamPermissions?.careTeamPermissions || [];

    const newRoleRules = roleKeys.map(nameKey => {
      const roleNumber = nameKey.match(/role-(\d{2})/)[1];
      const descKey = `role-${roleNumber}--description`;
      const name = extractedRolesData[nameKey] || '';
      const description = extractedRolesData[descKey] || '';

      const savedRole = savedRoleRules.find(r => r.name === name);

      return {
        name,
        description,
        permissions: savedRole ? hydratePermissions(savedRole.permissions) : [],
      };
    });

    updateRoleRulesData(newRoleRules);
    setIsInitialized(true);
  }, [
    formData.careTeamRoles,
    formData.careTeamPermissions,
    updateRoleRulesData,
    extractRolesData,
    isInitialized,
    hydratePermissions,
  ]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!formData.careTeamRoles) return;

    const extractedRolesData = extractRolesData(formData.careTeamRoles);
    if (!extractedRolesData) return;

    const roleKeys = Object.keys(extractedRolesData)
      .filter(key => key.match(/role-\d{2}--name/))
      .sort();

    if (roleKeys.length === 0) return;

    if (roleKeys.length !== roleRules.length) {
      const newRoleRules = roleKeys.map(nameKey => {
        const roleNumber = nameKey.match(/role-(\d{2})/)[1];
        const descKey = `role-${roleNumber}--description`;
        const name = extractedRolesData[nameKey] || '';
        const description = extractedRolesData[descKey] || '';

        const existingRole = roleRules.find(r => r.name === name);

        return {
          name,
          description,
          permissions: existingRole ? existingRole.permissions : [],
        };
      });

      updateRoleRulesData(newRoleRules);
      return;
    }

    const hasChanged = roleKeys.some((nameKey, index) => {
      const roleNumber = nameKey.match(/role-(\d{2})/)[1];
      const descKey = `role-${roleNumber}--description`;
      const name = extractedRolesData[nameKey] || '';
      const description = extractedRolesData[descKey] || '';

      return roleRules[index]?.name !== name || roleRules[index]?.description !== description;
    });

    if (hasChanged) {
      const updatedRoleRules = roleKeys.map((nameKey, index) => {
        const roleNumber = nameKey.match(/role-(\d{2})/)[1];
        const descKey = `role-${roleNumber}--description`;
        const name = extractedRolesData[nameKey] || '';
        const description = extractedRolesData[descKey] || '';

        return {
          name,
          description,
          permissions: roleRules[index]?.permissions || [],
        };
      });

      updateRoleRulesData(updatedRoleRules);
    }
  }, [formData.careTeamRoles, roleRules, updateRoleRulesData, extractRolesData, isInitialized]);

  useEffect(() => {
    if (!isInitialized || roleRules.length === 0) return;

    const needsHydration = roleRules.some(role =>
      role.permissions.some(
        p => p.type === 'rule' && !p.form && p.contentRef && contentRefMap.has(p.contentRef),
      ),
    );

    if (needsHydration) {
      const hydratedRoleRules = roleRules.map(role => ({
        ...role,
        permissions: hydratePermissions(role.permissions),
      }));
      updateRoleRulesData(hydratedRoleRules);
    }
  }, [
    allAvailableContent,
    roleRules,
    isInitialized,
    hydratePermissions,
    updateRoleRulesData,
    contentRefMap,
  ]);

  const addForms = useCallback(
    roleIndex => permissions => {
      updateRoleRulesData([
        ...roleRules.slice(0, roleIndex),
        {
          ...roleRules[roleIndex],
          permissions: [...roleRules[roleIndex].permissions, ...permissions],
        },
        ...roleRules.slice(roleIndex + 1),
      ]);
    },
    [roleRules, updateRoleRulesData],
  );

  const removePermission = useCallback(
    roleIndex => permissionId => {
      updateRoleRulesData([
        ...roleRules.slice(0, roleIndex),
        {
          ...roleRules[roleIndex],
          permissions: roleRules[roleIndex].permissions.filter(
            permission =>
              (permission.id ?? permission.form?.product_id ?? permission.form?.id) !==
              permissionId,
          ),
        },
        ...roleRules.slice(roleIndex + 1),
      ]);
    },
    [roleRules, updateRoleRulesData],
  );

  const updatePermissionType = useCallback(
    roleIndex => (permissionId, newPermissionType) => {
      updateRoleRulesData([
        ...roleRules.slice(0, roleIndex),
        {
          ...roleRules[roleIndex],
          permissions: roleRules[roleIndex].permissions.map(permission =>
            (permission.id ?? permission.form?.product_id ?? permission.form?.id) === permissionId
              ? { ...permission, permissionType: newPermissionType }
              : permission,
          ),
        },
        ...roleRules.slice(roleIndex + 1),
      ]);
    },
    [roleRules, updateRoleRulesData],
  );

  return (
    <>
      <Card>
        <p className="info-text">{t('pathways:ProcedurePathways.wizard.careTeamRules.info')}</p>
      </Card>
      {form.getFieldDecorator('careTeamPermissions')(<span />)}
      {roleRules.length === 0 ? (
        <Card.Half>
          <Alert
            description={t('pathways:ProcedurePathways.wizard.careTeamRules.noRoles.description')}
            message={t('pathways:ProcedurePathways.wizard.careTeamRules.noRoles.title')}
            showIcon
            type="info"
          />
        </Card.Half>
      ) : (
        roleRules.map(({ name, permissions }, index) => (
          <AddFormToRole
            allAvailableContent={allAvailableContent}
            callToActionList={callToActionList}
            customContent={allAvailableContent}
            key={`role-${index}`}
            selectedPermissions={permissions}
            title={name}
            addPermissions={addForms(index)}
            removePermission={removePermission(index)}
            updatePermissionType={updatePermissionType(index)}
          />
        ))
      )}
    </>
  );
}

export default CareTeamPermissions;
